import React, { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { BRANCHES, SKUS, CATEGORIES, DAILY_REVENUE } from "../lib/data";
import { Tile, Badge, StatusDot, KPI, Sparkline } from "../components/Primitives";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import { useChartColors } from "../lib/chartColors";
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip,
  CartesianGrid, ComposedChart, Line, RadialBarChart, RadialBar, PolarAngleAxis, Cell,
} from "recharts";
import {
  ArrowLeft, MapPin, Phone, User, AlertOctagon, TrendingUp, Package, Truck, Zap,
} from "lucide-react";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg">
      <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

// Deterministic per-branch synthesis
function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
  return Math.abs(h);
}

function buildBranchDetail(branch) {
  const seed = hashStr(branch.id);
  function rng(n) { return (Math.sin(seed * 7 + n) + 1) / 2; }

  // 90-day daily revenue
  const daily = Array.from({ length: 90 }).map((_, i) => {
    const base = branch.monthlyActual / 30;
    const noise = rng(i) * 0.4 + Math.sin(i * 0.4) * 0.18;
    return { day: i - 89, date: `D-${89 - i}`, revenue: Math.round(base * (1 + noise - 0.2)) };
  });

  // SKU coverage by category
  const skuByCategory = CATEGORIES.map((c, i) => ({
    name: c.name.split(" ")[0],
    inStock: Math.round(branch.skuCount * 0.25 * (0.85 + rng(i) * 0.18)),
    stockout: Math.round(branch.skuCount * 0.04 * (rng(i + 9) + 0.4) * (1 + branch.stockoutRate * 4)),
    overstock: Math.round(branch.skuCount * 0.05 * (rng(i + 17) + 0.3)),
  }));

  // Top 8 SKUs by revenue
  const topSkus = [...SKUS].slice(0, 12).map((s, i) => ({
    ...s,
    revenue: Math.round((branch.monthlyActual / 12) * (1.5 - i * 0.08) * (0.7 + rng(i + 21) * 0.6)),
    units: Math.round((s.monthlyUnits / 30) * (0.5 + rng(i + 31) * 1.2)),
    stockState: rng(i + 41) > 0.85 ? "stockout" : rng(i + 53) > 0.75 ? "low" : "healthy",
  })).sort((a, b) => b.revenue - a.revenue).slice(0, 8);

  // Recent transactions
  const txns = Array.from({ length: 8 }).map((_, i) => ({
    id: `TXN-${String(seed % 100000 + i).padStart(6, "0")}`,
    sku: SKUS[Math.floor(rng(i + 7) * SKUS.length)].name,
    qty: Math.floor(rng(i + 13) * 6) + 1,
    amount: Math.round(rng(i + 23) * 28000 + 800),
    channel: ["Physical", "Shopee", "Direct"][Math.floor(rng(i + 31) * 3)],
    customer: ["Walk-in", "B&G Auto Service", "CoolFix HVAC", "AC Doctor", "RefriPro", "MaxCool", "Walk-in"][Math.floor(rng(i + 41) * 7)],
    minutesAgo: Math.floor(rng(i + 51) * 480) + 2,
  }));

  // Channel mix
  const channelMix = [
    { name: "Physical", value: Math.round(branch.monthlyActual * (0.62 + rng(99) * 0.12)) },
    { name: "Shopee", value: Math.round(branch.monthlyActual * (0.14 + rng(103) * 0.06)) },
    { name: "Direct", value: Math.round(branch.monthlyActual * (0.12 + rng(107) * 0.05)) },
  ];

  return { daily, skuByCategory, topSkus, txns, channelMix };
}

export default function BranchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const C = useChartColors();
  const branch = BRANCHES.find(b => b.id === id);

  const detail = useMemo(() => branch ? buildBranchDetail(branch) : null, [branch]);

  if (!branch) {
    return (
      <div className="tile p-12 text-center">
        <h2 className="font-display text-xl font-semibold">Branch not found</h2>
        <p className="text-sm text-muted mt-2">No branch matches ID <span className="font-mono-tabular">{id}</span></p>
        <Link to="/branches" className="mt-4 inline-block text-primary text-sm hover:underline">← Back to all branches</Link>
      </div>
    );
  }

  const last7 = detail.daily.slice(-7).map(d => d.revenue);
  const last30Revenue = detail.daily.slice(-30).reduce((s, d) => s + d.revenue, 0);
  const prior30 = detail.daily.slice(-60, -30).reduce((s, d) => s + d.revenue, 0);
  const growth = ((last30Revenue - prior30) / prior30) * 100;

  const peerRank = [...BRANCHES].sort((a, b) => b.monthlyActual - a.monthlyActual).findIndex(b => b.id === branch.id) + 1;
  const totalChannel = detail.channelMix.reduce((s, x) => s + x.value, 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <button data-testid="back-to-branches" onClick={() => navigate("/branches")} className="text-[11px] uppercase tracking-widest text-muted hover:text-primary inline-flex items-center gap-1.5 mb-2">
            <ArrowLeft size={12} /> Branches
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{branch.name}</h1>
            <Badge status="neutral">{branch.brand}</Badge>
            <Badge status={branch.achievement >= 1 ? "success" : branch.achievement >= 0.9 ? "warning" : "critical"}>
              {(branch.achievement * 100).toFixed(0)}% of target
            </Badge>
          </div>
          <div className="text-sm text-muted mt-1.5 flex items-center gap-4 flex-wrap font-mono-tabular">
            <span className="inline-flex items-center gap-1.5"><MapPin size={12} /> {branch.region} · {branch.lat.toFixed(2)}°N, {branch.lng.toFixed(2)}°E</span>
            <span className="inline-flex items-center gap-1.5"><User size={12} /> Branch Head: {branch.manager}</span>
            <span className="inline-flex items-center gap-1.5"><Phone size={12} /> {branch.code}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-3 h-9 text-sm border border-border bg-surface-2 hover:bg-surface rounded-sm">Cycle Count</button>
          <button className="px-3 h-9 text-sm border border-border bg-surface-2 hover:bg-surface rounded-sm">Replenishment Request</button>
          <button className="px-3 h-9 text-sm bg-primary text-primary-fg rounded-sm">Export Brief</button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="tile grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-y md:divide-y-0 divide-border">
        <KPI testid="kpi-rev" label="Revenue MTD" value={formatPeso(branch.monthlyActual, { compact: true })}
             sub={`Target ${formatPeso(branch.monthlyTarget, { compact: true })}`}
             status={branch.achievement >= 1 ? "success" : branch.achievement >= 0.9 ? "warning" : "critical"}
             trend={(branch.achievement - 1) * 100} />
        <KPI label="National Rank" value={`#${peerRank}`} sub={`of ${BRANCHES.length}`} status={peerRank <= 25 ? "success" : peerRank <= 60 ? "info" : "warning"} />
        <KPI label="Stockout Rate" value={`${(branch.stockoutRate * 100).toFixed(1)}%`} sub="A-class target <5%" status={branch.stockoutRate > 0.06 ? "critical" : "success"} trend={-2.4} />
        <KPI label="Inventory Value" value={formatPeso(branch.inventoryValue, { compact: true })} sub={`${branch.skuCount} active SKUs`} />
        <KPI label="Days on Hand" value={branch.daysOnHand} sub="Target 30–45" status={branch.daysOnHand <= 45 ? "success" : "warning"} />
        <KPI label="Inv Accuracy" value={`${(branch.inventoryAccuracy * 100).toFixed(1)}%`} sub="Target >98%" status={branch.inventoryAccuracy >= 0.98 ? "success" : "warning"} />
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Revenue 90d */}
        <Tile className="col-span-12 lg:col-span-8" testid="revenue-90d" title="Revenue — 90 days"
              action={<Badge status={growth >= 0 ? "success" : "critical"}>{growth >= 0 ? "+" : ""}{growth.toFixed(1)}% vs prior 30d</Badge>}>
          <div className="grid grid-cols-4 gap-4 mb-4">
            <Stat label="Last 30d" value={formatPeso(last30Revenue, { compact: true })} />
            <Stat label="Prior 30d" value={formatPeso(prior30, { compact: true })} />
            <Stat label="Daily Avg" value={formatPeso(last30Revenue / 30, { compact: true })} />
            <Stat label="7-day Trend" value={<Sparkline data={last7} height={28} />} />
          </div>
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={detail.daily} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id={`rg-br-${branch.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.chart[0]} stopOpacity="0.45" />
                    <stop offset="100%" stopColor={C.chart[0]} stopOpacity="0" />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" />
                <XAxis dataKey="date" tick={tickStyle} stroke={C.borderStrong} interval={8} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `₱${(v / 1000).toFixed(0)}K`} />
                <Tooltip content={<TT formatter={v => formatPeso(v)} />} cursor={{ stroke: C.borderStrong }} />
                <Area type="monotone" dataKey="revenue" name="Revenue" stroke={C.chart[0]} strokeWidth={2} fill={`url(#rg-br-${branch.id})`} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        {/* Channel mix gauge */}
        <Tile className="col-span-12 lg:col-span-4" title="Channel Mix">
          <div className="space-y-3 mt-1">
            {detail.channelMix.map((c, i) => {
              const pctv = (c.value / totalChannel) * 100;
              return (
                <div key={c.name}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="inline-flex items-center gap-2 font-medium">
                      <span className="w-2 h-2 rounded-sm" style={{ background: C.chart[i] }} /> {c.name}
                    </span>
                    <span className="font-mono-tabular">
                      {formatPeso(c.value, { compact: true })}
                      <span className="text-muted ml-2">{pctv.toFixed(0)}%</span>
                    </span>
                  </div>
                  <div className="h-2 bg-surface-2 rounded-sm overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${pctv}%`, background: C.chart[i] }} />
                  </div>
                </div>
              );
            })}
          </div>
          <hr className="my-4 border-border" />
          <div>
            <div className="kpi-label mb-2">Headcount & Productivity</div>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Staff" value={branch.headcount} />
              <Stat label="Rev / Head" value={formatPeso(branch.monthlyActual / branch.headcount, { compact: true })} />
            </div>
          </div>
        </Tile>

        {/* SKU coverage by category */}
        <Tile className="col-span-12 lg:col-span-7" testid="sku-coverage" title="SKU Coverage by Category">
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={detail.skuByCategory} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="name" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} />
                <Tooltip content={<TT formatter={formatNumber} />} cursor={{ fill: C.surface2 }} />
                <Bar dataKey="inStock" name="In stock" stackId="a" fill={C.success} isAnimationActive={false} />
                <Bar dataKey="overstock" name="Overstock" stackId="a" fill={C.warning} isAnimationActive={false} />
                <Bar dataKey="stockout" name="Stockout" stackId="a" fill={C.critical} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        {/* Top SKUs */}
        <Tile className="col-span-12 lg:col-span-5" testid="top-skus" title="Top SKUs — MTD Revenue">
          <div className="space-y-0 divide-y divide-border -mx-4 -my-4">
            {detail.topSkus.map((s, i) => (
              <div key={s.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                <span className="font-mono-tabular text-xs text-subtle w-5">#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{s.name}</div>
                  <div className="text-[10px] text-muted font-mono-tabular">{s.id} · {s.category} · {s.abc}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono-tabular text-sm font-semibold">{formatPeso(s.revenue, { compact: true })}</div>
                  <div className="text-[10px] text-muted font-mono-tabular">{formatNumber(s.units)} u</div>
                </div>
                <Badge status={s.stockState === "stockout" ? "critical" : s.stockState === "low" ? "warning" : "success"}>
                  {s.stockState}
                </Badge>
              </div>
            ))}
          </div>
        </Tile>

        {/* Recent transactions */}
        <Tile className="col-span-12 lg:col-span-7" testid="recent-txns" title="Recent Transactions">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted">
                  <th className="text-left py-2 px-3 border-b border-border">Txn ID</th>
                  <th className="text-left py-2 px-3 border-b border-border">SKU</th>
                  <th className="text-left py-2 px-3 border-b border-border">Channel</th>
                  <th className="text-left py-2 px-3 border-b border-border">Customer</th>
                  <th className="text-right py-2 px-3 border-b border-border">Qty</th>
                  <th className="text-right py-2 px-3 border-b border-border">Amount</th>
                  <th className="text-right py-2 px-3 border-b border-border">When</th>
                </tr>
              </thead>
              <tbody className="font-mono-tabular">
                {detail.txns.map(t => (
                  <tr key={t.id} className="border-b border-border last:border-b-0 hover:bg-surface-2">
                    <td className="py-2 px-3 text-xs">{t.id}</td>
                    <td className="py-2 px-3 font-sans text-xs truncate max-w-xs">{t.sku}</td>
                    <td className="py-2 px-3"><Badge status="neutral">{t.channel}</Badge></td>
                    <td className="py-2 px-3 font-sans text-xs">{t.customer}</td>
                    <td className="py-2 px-3 text-right">{t.qty}</td>
                    <td className="py-2 px-3 text-right">{formatPeso(t.amount)}</td>
                    <td className="py-2 px-3 text-right text-muted">{t.minutesAgo}m ago</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tile>

        {/* AI Actions */}
        <Tile className="col-span-12 lg:col-span-5" testid="ai-actions" title="AI Branch Recommendations"
              action={<Badge status="info"><Zap size={11} /> Auto-generated</Badge>}>
          <div className="space-y-3">
            <Action icon={<Package size={14} />} severity={branch.stockoutRate > 0.06 ? "critical" : "info"}
                    title={branch.stockoutRate > 0.06 ? "Emergency replenishment" : "Healthy inventory"}
                    body={branch.stockoutRate > 0.06
                      ? `Stockout rate ${(branch.stockoutRate * 100).toFixed(1)}% exceeds 6% threshold. Draft transfer-in from nearest hub for top 8 A-class SKUs.`
                      : "Stockout rate within tolerance. Maintain current ordering cadence."} />
            <Action icon={<TrendingUp size={14} />} severity={branch.achievement >= 1 ? "success" : "warning"}
                    title={branch.achievement >= 1 ? "Above target — scale assortment" : "Below target — diagnose conversion"}
                    body={branch.achievement >= 1
                      ? `Achievement ${(branch.achievement * 100).toFixed(0)}%. Consider adding B-class SKU depth in HVAC + Refrigeration to capture upside.`
                      : `Achievement ${(branch.achievement * 100).toFixed(0)}%. Audit walk-in conversion, technician customer concentration, and local promo plan.`} />
            <Action icon={<Truck size={14} />} severity={branch.daysOnHand > 60 ? "warning" : "success"}
                    title={branch.daysOnHand > 60 ? "Working capital opportunity" : "Inventory velocity healthy"}
                    body={branch.daysOnHand > 60
                      ? `${branch.daysOnHand} days on hand vs 45-day target. Recommend SKU rationalization on bottom-quartile velocity items.`
                      : `${branch.daysOnHand} days on hand — efficient capital deployment.`} />
            <Action icon={<AlertOctagon size={14} />} severity={branch.inventoryAccuracy < 0.95 ? "critical" : "success"}
                    title={branch.inventoryAccuracy < 0.95 ? "Cycle count overdue" : "Inventory accuracy strong"}
                    body={`Inventory accuracy ${(branch.inventoryAccuracy * 100).toFixed(1)}%. ${branch.inventoryAccuracy < 0.95 ? "Schedule full cycle count this week — accuracy below 95% threshold." : "Continue monthly cycle counting cadence."}`} />
          </div>
        </Tile>
      </div>
    </div>
  );
}

function Stat({ label, value, status }) {
  return (
    <div>
      <div className="kpi-label flex items-center gap-1.5">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-base mt-1">{value}</div>
    </div>
  );
}

function Action({ icon, severity, title, body }) {
  return (
    <div className={cn("border rounded-sm p-3 flex gap-3",
      severity === "critical" && "border-critical/30 bg-critical/5",
      severity === "warning" && "border-warning/30 bg-warning/5",
      severity === "success" && "border-success/30 bg-success/5",
      severity === "info" && "border-info/30 bg-info/5")}>
      <div className={cn("w-7 h-7 rounded-sm flex items-center justify-center shrink-0",
        severity === "critical" && "bg-critical/15 text-critical",
        severity === "warning" && "bg-warning/15 text-warning",
        severity === "success" && "bg-success/15 text-success",
        severity === "info" && "bg-info/15 text-info")}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm">{title}</div>
        <p className="text-[11px] text-muted leading-relaxed mt-0.5">{body}</p>
      </div>
    </div>
  );
}
